import { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus,
  Wallet,
  Search,
  CheckCircle2,
  XCircle,
  History,
  Trash2,
  FileText,
  Printer,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { deriveSalary } from "../lib/salary";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate } from "../lib/date";
import { uploadFile } from "../lib/files";
import AuthImage from "../components/AuthImage";
import SignaturePad, { SignatureImage, printElement } from "../components/SignaturePad";

const statusMap = {
  active:    { label: "Activo",    cls: "bg-amber-50 text-amber-700 border-amber-200" },
  paid:      { label: "Saldado",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelado", cls: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

const money = (n) =>
  `₡ ${Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const emptyForm = {
  branch_id: "",
  employee_id: "",
  amount: "",
  mode: "installments",
  installments: 4,
  reason: "",
  notes: "",
  date_issued: new Date().toISOString().slice(0, 10),
  signature: null,
  signature_name: "",
  signature_id: "",
  payment_method: "efectivo",
  payment_sinpe_phone: "",
  payment_bank: "",
  payment_voucher_number: "",
  payment_voucher_file: null,
  payment_voucher_path: "",
};

const ART173_MAX_RATIO = 0.25;
const ART173_MIN_INSTALLMENTS = 4;

const PAYMENT_METHODS = [
  { value: "efectivo", label: "Efectivo" },
  { value: "sinpe", label: "Sinpe Móvil" },
  { value: "transferencia", label: "Transferencia bancaria" },
];

const CR_BANKS = [
  "Banco Nacional (BN)",
  "Banco de Costa Rica (BCR)",
  "BAC Credomatic",
  "Banco Popular",
  "Banco Promérica",
  "Davivienda",
  "Scotiabank",
  "Coopenae",
  "Coopealianza",
  "Otro",
];

export default function Vales() {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [receiptTarget, setReceiptTarget] = useState(null);
  const receiptRef = useRef(null);

  const selectedEmployee = employees.find((e) => e.id === form.employee_id);
  // Salario MENSUAL equivalente según fijación (diaria => base×30, mensual => base).
  const selMonthly = selectedEmployee
    ? deriveSalary(selectedEmployee.base_salary, selectedEmployee.salary_fixation).monthly
    : 0;
  const maxAllowed = selMonthly ? selMonthly * ART173_MAX_RATIO : null;
  const amountExceedsCap = maxAllowed && Number(form.amount) > maxAllowed;
  const installmentsInvalid = form.mode === "installments" && Number(form.installments) < ART173_MIN_INSTALLMENTS;

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterBranch !== "all") params.branch_id = filterBranch;
      if (filterStatus !== "all") params.status = filterStatus;
      const [vRes, bRes, eRes, sRes] = await Promise.all([
        api.get("/vales", { params }),
        api.get("/branches"),
        api.get("/employees", { params: { limit: 1000 } }),
        api.get("/vales/summary"),
      ]);
      setItems(vRes.data);
      setBranches(bRes.data);
      setEmployees(eRes.data.items || []);
      setSummary(sRes.data);
    } catch {
      toast.error("Error al cargar vales");
    } finally {
      setLoading(false);
    }
  }, [filterBranch, filterStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = items.filter((v) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (v.employee_name || "").toLowerCase().includes(s) ||
      (v.employee_code || "").toLowerCase().includes(s) ||
      (v.reason || "").toLowerCase().includes(s)
    );
  });

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (v) => {
    setForm({
      branch_id: v.branch_id || "",
      employee_id: v.employee_id,
      amount: String(v.amount),
      mode: v.mode,
      installments: v.installments || 1,
      reason: v.reason || "",
      notes: v.notes || "",
      date_issued: v.date_issued,
    });
    setEditingId(v.id);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.branch_id || !form.employee_id || !form.amount || Number(form.amount) <= 0) {
      toast.error("Sucursal, empleado y monto son requeridos");
      return;
    }
    if (!editingId && !form.signature) {
      toast.error("El trabajador debe firmar el recibo del vale (Art. 173 CT)");
      return;
    }
    if (amountExceedsCap) {
      toast.error(`Monto excede 25% del salario (Art. 173 CT). Máx: ${money(maxAllowed)}`);
      return;
    }
    if (installmentsInvalid) {
      toast.error(`Cuotas insuficientes. Art. 173 CT exige mínimo ${ART173_MIN_INSTALLMENTS}.`);
      return;
    }
    if (!editingId) {
      if (form.payment_method === "sinpe" && (!form.payment_sinpe_phone || !form.payment_voucher_number)) {
        toast.error("Sinpe requiere teléfono destino y número de comprobante");
        return;
      }
      if (form.payment_method === "transferencia" && (!form.payment_bank || !form.payment_voucher_number)) {
        toast.error("Transferencia requiere banco y número de comprobante");
        return;
      }
    }
    const payload = {
      employee_id: form.employee_id,
      amount: Number(form.amount),
      mode: form.mode,
      installments: form.mode === "installments" ? Number(form.installments) || 4 : undefined,
      reason: form.reason,
      notes: form.notes,
      date_issued: form.date_issued,
    };
    if (!editingId) {
      payload.signature = form.signature;
      payload.signature_name = form.signature_name || selectedEmployee?.full_name || "";
      payload.signature_id = form.signature_id || selectedEmployee?.id_number || "";
      payload.payment_method = form.payment_method;
      payload.payment_sinpe_phone = form.payment_sinpe_phone;
      payload.payment_bank = form.payment_bank;
      payload.payment_voucher_number = form.payment_voucher_number;
    }
    try {
      let voucher_path = form.payment_voucher_path || "";
      if (!editingId && form.payment_voucher_file) {
        const rec = await uploadFile(form.payment_voucher_file, {
          category: "vale-voucher",
          entityId: form.employee_id,
        });
        voucher_path = rec.storage_path;
      }
      let created;
      if (editingId) {
        await api.patch(`/vales/${editingId}`, payload);
        toast.success("Vale actualizado");
      } else {
        payload.payment_voucher_path = voucher_path;
        const { data } = await api.post("/vales", payload);
        created = data;
        toast.success("Vale registrado y firmado");
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      fetchData();
      if (created) setReceiptTarget(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await api.post(`/vales/${cancelTarget.id}/cancel`, { reason: "RRHH cancelled" });
      toast.success("Vale cancelado");
      setCancelTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cancelar");
    }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Eliminar vale de ${money(v.amount)} para ${v.employee_name}?`)) return;
    try {
      await api.delete(`/vales/${v.id}`);
      toast.success("Vale eliminado");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div data-testid="vales-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            Vales
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Adelantos de salario con deducción automática en la planilla
          </p>
        </div>
        <Button onClick={openCreate} className="bg-zinc-900 text-white hover:bg-zinc-800" data-testid="add-vale-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo vale
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="vales-summary">
          <Card className="bg-white border-zinc-200"><CardContent className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-500">Saldo activo</p>
            <p className="text-xl font-bold text-zinc-950 mt-1 font-mono">{money(summary.total_active_balance)}</p>
            <p className="text-[11px] text-zinc-400 mt-1">{summary.count_active} vale{summary.count_active !== 1 ? "s" : ""}</p>
          </CardContent></Card>
          <Card className="bg-white border-zinc-200"><CardContent className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-500">Total saldado</p>
            <p className="text-xl font-bold text-zinc-950 mt-1 font-mono">{money(summary.total_paid)}</p>
            <p className="text-[11px] text-zinc-400 mt-1">{summary.count_paid} vale{summary.count_paid !== 1 ? "s" : ""}</p>
          </CardContent></Card>
          <Card className="bg-white border-zinc-200"><CardContent className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Cancelados</p>
            <p className="text-xl font-bold text-zinc-500 mt-1 font-mono">{summary.count_cancelled}</p>
          </CardContent></Card>
          <Card className="bg-white border-zinc-200"><CardContent className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Total historico</p>
            <p className="text-xl font-bold text-zinc-950 mt-1 font-mono">{summary.count_total}</p>
          </CardContent></Card>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            className="pl-9"
            placeholder="Buscar por empleado o motivo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="vale-search-input"
          />
        </div>
        <Select value={filterBranch} onValueChange={setFilterBranch}>
          <SelectTrigger className="w-[180px]" data-testid="vale-filter-branch">
            <SelectValue placeholder="Sucursal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las sucursales</SelectItem>
            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]" data-testid="vale-filter-status">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="paid">Saldados</SelectItem>
            <SelectItem value="cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Fecha</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Empleado</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Modo</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">Monto</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">Cuota</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">Saldo</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Estado</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((v) => (
                <TableRow
                  key={v.id}
                  className="border-zinc-100 hover:bg-zinc-50/80 transition-colors cursor-pointer"
                  onClick={() => setDetail(v)}
                  data-testid={`vale-row-${v.id}`}
                >
                  <TableCell className="text-sm text-zinc-600 font-mono whitespace-nowrap">{formatDate(v.date_issued)}</TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm text-zinc-900 font-medium">{v.employee_name}</p>
                      <p className="text-[11px] text-zinc-400 font-mono">{v.employee_code}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {v.mode === "single" ? (
                      <Badge variant="outline" className="bg-zinc-100 text-zinc-700 border-zinc-200">Pago único</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {v.installments_paid || 0}/{v.installments} cuotas
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm text-zinc-900 font-semibold font-mono">{money(v.amount)}</TableCell>
                  <TableCell className="text-right text-sm text-zinc-700 font-mono">{money(v.installment_amount)}</TableCell>
                  <TableCell className="text-right text-sm font-bold font-mono">
                    <span className={v.remaining_amount > 0 ? "text-amber-700" : "text-zinc-400"}>
                      {money(v.remaining_amount)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`font-medium ${statusMap[v.status]?.cls || ""}`}>
                      {statusMap[v.status]?.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setReceiptTarget(v)}
                        title="Ver recibo"
                        data-testid={`view-receipt-${v.id}`}
                      >
                        <FileText className="w-4 h-4 text-zinc-400" />
                      </Button>
                      {v.status === "active" && v.paid_amount === 0 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEdit(v)}
                          title="Editar"
                          data-testid={`edit-vale-${v.id}`}
                        >
                          <Plus className="w-4 h-4 rotate-45 text-zinc-400" />
                        </Button>
                      )}
                      {v.status === "active" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setCancelTarget(v)}
                          title="Cancelar"
                          data-testid={`cancel-vale-${v.id}`}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      )}
                      {v.paid_amount === 0 && v.status !== "paid" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDelete(v)}
                          title="Eliminar"
                          data-testid={`delete-vale-${v.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-16 text-zinc-400">
                    <Wallet className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No hay vales registrados</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Vale receipt (printable) */}
      <Dialog open={!!receiptTarget} onOpenChange={(o) => !o && setReceiptTarget(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">Recibo de Vale (adelanto de salario)</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Comprobante firmado. Puede imprimirlo para el expediente del trabajador.
            </DialogDescription>
          </DialogHeader>
          <div ref={receiptRef} data-testid="vale-receipt" className="p-6 bg-white text-zinc-950 text-[13px] leading-relaxed">
            <div className="flex items-start justify-between border-b-2 border-zinc-900 pb-3 mb-4">
              <div className="flex items-center gap-3">
                <img src="/vetcomer-logo.png" alt="VETCOMER" className="w-10 h-10 object-contain" />
                <div>
                  <p className="text-lg font-bold leading-tight">VETCOMER LTDA</p>
                  <p className="text-[11px] text-zinc-500">Cédula jurídica: [pendiente]</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-wider text-zinc-400">Recibo de Vale</p>
                <p className="font-mono text-xs text-zinc-600">#{receiptTarget?.id?.slice(0, 8).toUpperCase()}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">{formatDate(receiptTarget?.date_issued)}</p>
              </div>
            </div>
            <h2 className="text-center font-bold uppercase text-sm tracking-wide mb-3">
              Autorización de deducción salarial — Art. 173 Código de Trabajo CR
            </h2>
            <p className="mb-3 text-justify">
              De conformidad con el <strong>Artículo 173 del Código de Trabajo de la República de Costa Rica</strong>,
              yo <strong>{receiptTarget?.signature_name || receiptTarget?.employee_name}</strong>
              {receiptTarget?.signature_id ? `, portador(a) de la cédula ${receiptTarget.signature_id}` : ""},
              declaro haber recibido del patrono <strong>VETCOMER LTDA</strong> la suma de
              {" "}<strong>{money(receiptTarget?.amount)}</strong> en calidad de
              <strong> adelanto de salario (VALE)</strong> en la fecha
              {" "}<strong>{formatDate(receiptTarget?.date_issued)}</strong>.
            </p>
            <div className="grid grid-cols-2 gap-3 bg-zinc-50 border border-zinc-200 rounded p-3 text-[12px] mb-3">
              <div><span className="text-zinc-500">Modalidad:</span> <strong>{receiptTarget?.mode === "single" ? "Pago único" : "Cuotas"}</strong></div>
              <div><span className="text-zinc-500">Monto total:</span> <strong>{money(receiptTarget?.amount)}</strong></div>
              {receiptTarget?.mode === "installments" && (
                <>
                  <div><span className="text-zinc-500">Cantidad de cuotas:</span> <strong>{receiptTarget?.installments}</strong></div>
                  <div><span className="text-zinc-500">Monto por cuota:</span> <strong>{money(receiptTarget?.installment_amount)}</strong></div>
                </>
              )}
              {receiptTarget?.reason && (
                <div className="col-span-2"><span className="text-zinc-500">Motivo:</span> {receiptTarget.reason}</div>
              )}
            </div>

            {receiptTarget?.payment_method && (
              <div className="bg-zinc-50 border border-zinc-200 rounded p-3 text-[12px] mb-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-zinc-500 mb-1">Entrega del dinero</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>Método: <strong>
                    {receiptTarget.payment_method === "efectivo" && "Efectivo"}
                    {receiptTarget.payment_method === "sinpe" && "Sinpe Móvil"}
                    {receiptTarget.payment_method === "transferencia" && "Transferencia"}
                  </strong></div>
                  {receiptTarget.payment_method === "sinpe" && (
                    <div>Teléfono: <strong>{receiptTarget.payment_sinpe_phone}</strong></div>
                  )}
                  {receiptTarget.payment_method === "transferencia" && (
                    <div>Banco: <strong>{receiptTarget.payment_bank}</strong></div>
                  )}
                  {receiptTarget.payment_voucher_number && (
                    <div className="col-span-2">N° Comprobante: <strong>{receiptTarget.payment_voucher_number}</strong></div>
                  )}
                </div>
                {(receiptTarget.payment_voucher_path || receiptTarget.payment_voucher_image) && (
                  <div className="mt-2">
                    <p className="text-[10px] text-zinc-500 mb-1">Comprobante de pago:</p>
                    {receiptTarget.payment_voucher_path ? (
                      <AuthImage
                        path={receiptTarget.payment_voucher_path}
                        alt="Comprobante"
                        className="max-h-48 rounded border border-zinc-300 object-contain"
                      />
                    ) : (
                      <img
                        src={receiptTarget.payment_voucher_image}
                        alt="Comprobante"
                        className="max-h-48 rounded border border-zinc-300"
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            <p className="mb-3 text-justify">
              Autorizo expresamente al patrono a deducir de mi salario ordinario las cuotas indicadas
              en cada período de pago, hasta la cancelación total del adelanto. El presente anticipo
              <strong> no devenga intereses</strong>. En caso de terminación de la relación laboral,
              autorizo la aplicación del saldo pendiente sobre cualquier prestación o liquidación que
              me corresponda, en estricto apego al Código de Trabajo.
            </p>
            <p className="mb-6 text-justify">
              El monto adelantado respeta el límite de la cuarta parte (25%) del salario mensual
              convenido, según lo establece el Art. 173 CT.
            </p>
            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="text-center">
                <SignatureImage src={receiptTarget?.signature} height={80} />
                <p className="text-[11px] text-zinc-600 mt-1">
                  <strong>{receiptTarget?.signature_name || receiptTarget?.employee_name}</strong>
                </p>
                {receiptTarget?.signature_id && <p className="text-[10px] text-zinc-500">Céd: {receiptTarget.signature_id}</p>}
                <p className="text-[10px] text-zinc-400">Firma del trabajador</p>
              </div>
              <div className="text-center">
                <div className="h-20 border-b border-zinc-300" />
                <p className="text-[11px] text-zinc-600 mt-1"><strong>{receiptTarget?.authorized_by || "RRHH"}</strong></p>
                <p className="text-[10px] text-zinc-400">Autorizado por el patrono</p>
              </div>
            </div>
            <p className="text-[10px] text-zinc-400 text-center mt-6 italic">
              Firmado el {receiptTarget?.signed_at?.slice(0, 19).replace("T", " ")} · VETCOMER Workforce System
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptTarget(null)} data-testid="vale-receipt-close">
              Cerrar
            </Button>
            <Button
              onClick={() => printElement(receiptRef.current, `Recibo Vale ${receiptTarget?.id?.slice(0, 8)}`)}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="vale-receipt-print"
            >
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              {editingId ? "Editar vale" : "Nuevo vale"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              El vale se deducirá automáticamente en la próxima planilla que se calcule.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium text-zinc-700">Sucursal *</Label>
              <Select
                value={form.branch_id}
                onValueChange={(v) => setForm({ ...form, branch_id: v, employee_id: "" })}
                disabled={!!editingId}
              >
                <SelectTrigger data-testid="vale-branch-select"><SelectValue placeholder="Seleccionar sucursal" /></SelectTrigger>
                <SelectContent>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">Empleado *</Label>
              <Select
                value={form.employee_id}
                onValueChange={(v) => setForm({ ...form, employee_id: v })}
                disabled={!!editingId || !form.branch_id}
              >
                <SelectTrigger data-testid="vale-employee-select">
                  <SelectValue placeholder={form.branch_id ? "Seleccionar empleado" : "Primero elige una sucursal"} />
                </SelectTrigger>
                <SelectContent>
                  {employees
                    .filter((e) => !form.branch_id || e.primary_branch_id === form.branch_id)
                    .map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.full_name} · {e.employee_code}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-zinc-700">Monto (CRC) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="50000"
                  data-testid="vale-amount-input"
                  className={amountExceedsCap ? "border-red-300" : ""}
                />
                {maxAllowed != null && (
                  <p className={`text-[11px] mt-1 ${amountExceedsCap ? "text-red-600 font-medium" : "text-zinc-500"}`}>
                    Máx. legal (Art. 173 CT, 25% del salario): {money(maxAllowed)}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">Fecha</Label>
                <Input
                  type="date"
                  value={form.date_issued}
                  onChange={(e) => setForm({ ...form, date_issued: e.target.value })}
                  data-testid="vale-date-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">Modo de deducción *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Button
                  type="button"
                  variant={form.mode === "single" ? "default" : "outline"}
                  className={form.mode === "single" ? "bg-zinc-900 hover:bg-zinc-800 text-white" : ""}
                  onClick={() => setForm({ ...form, mode: "single" })}
                  data-testid="vale-mode-single"
                >
                  Pago único
                </Button>
                <Button
                  type="button"
                  variant={form.mode === "installments" ? "default" : "outline"}
                  className={form.mode === "installments" ? "bg-zinc-900 hover:bg-zinc-800 text-white" : ""}
                  onClick={() => setForm({ ...form, mode: "installments" })}
                  data-testid="vale-mode-installments"
                >
                  Cuotas
                </Button>
              </div>
            </div>
            {form.mode === "installments" && (
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Número de cuotas (mín. {ART173_MIN_INSTALLMENTS} · Art. 173 CT)
                </Label>
                <Input
                  type="number"
                  min={ART173_MIN_INSTALLMENTS}
                  max="24"
                  value={form.installments}
                  onChange={(e) => setForm({ ...form, installments: e.target.value })}
                  data-testid="vale-installments-input"
                  className={installmentsInvalid ? "border-red-300" : ""}
                />
                {installmentsInvalid && (
                  <p className="text-[11px] text-red-600 mt-1">
                    Mínimo {ART173_MIN_INSTALLMENTS} cuotas según Art. 173 del Código de Trabajo.
                  </p>
                )}
                {form.amount && form.installments > 0 && !installmentsInvalid && (
                  <p className="text-[11px] text-zinc-500 mt-1">
                    Cuota estimada: {money(Number(form.amount) / Number(form.installments))} por planilla
                  </p>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-zinc-700">Motivo</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Ej: Emergencia médica"
                data-testid="vale-reason-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">Notas</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="vale-notes-input"
              />
            </div>
            {!editingId && (
              <>
                {/* Método de pago */}
                <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 space-y-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600">Entrega del dinero al trabajador</p>
                  <div>
                    <Label className="text-xs font-medium text-zinc-700">Método de pago *</Label>
                    <Select
                      value={form.payment_method}
                      onValueChange={(v) => setForm({
                        ...form,
                        payment_method: v,
                        payment_sinpe_phone: "",
                        payment_bank: "",
                        payment_voucher_number: "",
                      })}
                    >
                      <SelectTrigger data-testid="vale-payment-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {form.payment_method === "sinpe" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-medium text-zinc-700">Teléfono destino *</Label>
                        <Input
                          value={form.payment_sinpe_phone}
                          onChange={(e) => setForm({ ...form, payment_sinpe_phone: e.target.value })}
                          placeholder="8888-8888"
                          data-testid="vale-sinpe-phone"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-zinc-700">N° comprobante *</Label>
                        <Input
                          value={form.payment_voucher_number}
                          onChange={(e) => setForm({ ...form, payment_voucher_number: e.target.value })}
                          placeholder="SINPE-..."
                          data-testid="vale-voucher-number"
                        />
                      </div>
                    </div>
                  )}

                  {form.payment_method === "transferencia" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-medium text-zinc-700">Banco *</Label>
                        <Select
                          value={form.payment_bank}
                          onValueChange={(v) => setForm({ ...form, payment_bank: v })}
                        >
                          <SelectTrigger data-testid="vale-bank-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                          <SelectContent>
                            {CR_BANKS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-zinc-700">N° comprobante *</Label>
                        <Input
                          value={form.payment_voucher_number}
                          onChange={(e) => setForm({ ...form, payment_voucher_number: e.target.value })}
                          placeholder="REF-..."
                          data-testid="vale-voucher-number"
                        />
                      </div>
                    </div>
                  )}

                  {/* Upload de comprobante (opcional) */}
                  <div>
                    <Label className="text-xs font-medium text-zinc-700">
                      Foto del comprobante <span className="text-zinc-400 font-normal">(opcional, máx 5 MB)</span>
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="file"
                        accept="image/*,application/pdf"
                        data-testid="vale-voucher-file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error("El archivo no debe pasar de 5 MB");
                            e.target.value = "";
                            return;
                          }
                          setForm({ ...form, payment_voucher_file: file });
                        }}
                      />
                      {form.payment_voucher_file && (
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, payment_voucher_file: null })}
                          className="text-[11px] text-red-500 hover:text-red-700"
                        >
                          Quitar
                        </button>
                      )}
                    </div>
                    {form.payment_voucher_file && form.payment_voucher_file.type?.startsWith("image/") && (
                      <img
                        src={URL.createObjectURL(form.payment_voucher_file)}
                        alt="Vista previa del comprobante"
                        className="mt-2 max-h-32 rounded border border-zinc-200"
                      />
                    )}
                    <p className="text-[10px] text-zinc-400 mt-1">
                      Se guardará en almacenamiento permanente al registrar el vale.
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-[11px] text-amber-900 leading-relaxed">
                  <p className="font-semibold mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Autorización legal (Art. 173 CT CR)
                  </p>
                  <p>
                    Al firmar, el trabajador autoriza expresamente al patrono a deducir de su salario
                    las cuotas correspondientes al presente adelanto hasta la cancelación total del
                    monto recibido, sin devengar intereses. El saldo pendiente podrá aplicarse a
                    cualquier liquidación si termina la relación laboral.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-zinc-700">Nombre del firmante</Label>
                    <Input
                      value={form.signature_name}
                      onChange={(e) => setForm({ ...form, signature_name: e.target.value })}
                      placeholder={selectedEmployee?.full_name || "Nombre completo"}
                      data-testid="vale-signer-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-zinc-700">Cédula</Label>
                    <Input
                      value={form.signature_id}
                      onChange={(e) => setForm({ ...form, signature_id: e.target.value })}
                      placeholder={selectedEmployee?.id_number || "1-XXXX-XXXX"}
                      data-testid="vale-signer-id"
                    />
                  </div>
                </div>
                <SignaturePad
                  value={form.signature}
                  onChange={(sig) => setForm({ ...form, signature: sig })}
                  label="Firma del trabajador *"
                  placeholder="Firme aquí con el dedo o lápiz óptico"
                  testid="vale-signature"
                />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="vale-cancel-button">Cancelar</Button>
            <Button
              onClick={handleSubmit}
              disabled={!editingId && (!form.signature || amountExceedsCap || installmentsInvalid)}
              className="bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40"
              data-testid="vale-submit-button"
            >
              {editingId ? "Guardar" : "Firmar y registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / history modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">Detalle del vale</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {detail?.employee_name} · {detail?.employee_code}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="py-2 space-y-3 text-sm">
              <div className="bg-zinc-50 rounded-lg p-3 border border-zinc-200 space-y-1.5 text-xs">
                <Row label="Monto total" value={money(detail.amount)} />
                <Row label="Modo" value={detail.mode === "single" ? "Pago único" : `Cuotas (${detail.installments_paid || 0}/${detail.installments})`} />
                <Row label="Cuota" value={money(detail.installment_amount)} />
                <Row label="Pagado" value={money(detail.paid_amount)} />
                <Row label="Saldo" value={money(detail.remaining_amount)} bold />
                <Row label="Fecha" value={formatDate(detail.date_issued)} />
                {detail.reason && <Row label="Motivo" value={detail.reason} />}
                {detail.cancel_reason && <Row label="Cancelación" value={detail.cancel_reason} />}
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1">
                  <History className="w-3 h-3" /> Historial de pagos
                </p>
                {(detail.payments || []).length === 0 ? (
                  <p className="text-xs text-zinc-400 italic">Aún no se ha aplicado ningún pago</p>
                ) : (
                  <div className="divide-y divide-zinc-100 text-xs">
                    {detail.payments.map((p, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5">
                        <div>
                          <p className="text-zinc-700 font-medium">{p.period_label}</p>
                          <p className="text-[10px] text-zinc-400 font-mono">{formatDate(p.date)}</p>
                        </div>
                        <span className="font-mono text-emerald-700 font-semibold">-{money(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="vale-detail-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirm */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar vale</AlertDialogTitle>
            <AlertDialogDescription>
              El vale de <strong>{cancelTarget?.employee_name}</strong> dejará de deducirse en futuras planillas.
              El saldo pagado hasta ahora ({money(cancelTarget?.paid_amount || 0)}) se mantiene en el historial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} className="bg-red-600 hover:bg-red-700">
              Cancelar vale
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-500">{label}</span>
      <span className={bold ? "font-bold text-zinc-900 font-mono" : "text-zinc-800 font-mono"}>{value}</span>
    </div>
  );
}
